import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import ConfirmDeleteModal from '@/components/ConfirmDeleteModal';
import ConfirmActionModal from '@/components/ConfirmActionModal';
import { AdminPageSections, AutoRefreshToolbar, CollapsiblePanel } from '@/components/common';
import JobProgressCard from '@/components/jobs/JobProgressCard';
import RecentJobsPanel from '@/components/jobs/RecentJobsPanel';
import PageHeader from '@/components/PageHeader';
import FloatingActionButton from '@/components/navigation/FloatingActionButton';
import { useNavigation } from '@/context/NavigationContext';
import SearchAdminFormPanels from '@/components/searches/SearchAdminFormPanels';
import SearchSharePanel from '@/components/searches/SearchSharePanel';
import SearchAclPanel from '@/components/searches/SearchAclPanel';
import SearchVisibilityPanel from '@/components/searches/SearchVisibilityPanel';
import SearchTransferDialog from '@/components/searches/SearchTransferDialog';
import { DataTable, TruncatedCell, TableActionBar, TABLE_ACTION_MIN_WIDTH, tableActionsColumnClass, type DataTableColumn } from '@/components/table';
import { EmptyState, ErrorBanner, ExportButtons, FilterSelect, SourceChips, StatusBadge, Toolbar } from '@/components/ui';
import { useToast } from '@/components/Toast';
import { useMe } from '@/context/MeContext';
import { useAutoRefreshPolling } from '@/hooks/useAutoRefreshPolling';
import { planWarningMessage } from '@/utils/planWarning';
import { canExportSearches } from '@/utils/navCapabilities';
import { runSearchErrorMessage } from '@/utils/runSearchError';
import { humanizeReason } from '@/utils/lifecycleReasons';
import { resolveCapabilities, searchPlanValidationLimits, shouldShowPlanLimitWarning } from '@/utils/capabilities';
import {
  buildSearchFilters,
  clearIrrelevantFields,
  duplicateSearchToAdminForm,
  resetSearchFormForCreate,
  searchToAdminForm,
  validateSearchForm,
  type SearchAdminFormState,
} from '@/utils/searchAdminForm';
import { workspacePickersAsUsers } from '@/api/services/workspaceUsers';
import { searchSourceLimitMessage } from '@/utils/sourceLimits';
import { visibilityBadgeLabel } from '@/utils/recipientVisibility';
import {
  canEditSearchVisibility,
  showWorkspaceVisibilityOption,
} from '@/utils/searchVisibility';
import type { Category, MeQuotas, Search, Source, User } from '@/types';

const PANEL_SCOPE = 'searches-page';

export default function SearchesPage() {
  const { toast } = useToast();
  const { me, loading: meLoading } = useMe();
  const readOnly = meLoading || (me?.permissions.readOnly ?? true);
  const canCreate = !meLoading && (me?.permissions.canCreateSearch ?? false);
  const canRun = !meLoading && (me?.permissions.canRunSearch ?? me?.permissions.canCreateSearch ?? false);
  const { isCompactNav } = useNavigation();
  const canExport = !meLoading && canExportSearches(me);
  const isAdmin = me?.permissions.canManageSources ?? false;
  const canManageUsers = me?.permissions.canManageUsers ?? false;
  const canManageWorkspaceMembers = me?.permissions.canManageWorkspaceMembers ?? false;
  const showWorkspaceVisibility = showWorkspaceVisibilityOption(me);
  const [searches, setSearches] = useState<Search[]>([]);
  const [teamUsers, setTeamUsers] = useState<User[]>([]);
  const [ownerFilter, setOwnerFilter] = useState('');
  const [quotas, setQuotas] = useState<MeQuotas | null>(null);
  const [allSources, setAllSources] = useState<Source[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [form, setForm] = useState<SearchAdminFormState>(() => resetSearchFormForCreate('vehicles', me));
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [runningId, setRunningId] = useState<string | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [activeSearchId, setActiveSearchId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [transferSearch, setTransferSearch] = useState<Search | null>(null);
  const [scrapeWarningId, setScrapeWarningId] = useState<string | null>(null);
  const [visibilityDraft, setVisibilityDraft] = useState<'private' | 'workspace'>('private');
  const [savedVisibility, setSavedVisibility] = useState<'private' | 'workspace'>('private');
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [visibilityStatus, setVisibilityStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const fetchSearches = useCallback(async () => {
    const listParams = canManageUsers && ownerFilter ? { owner_user_id: ownerFilter } : undefined;
    const [s, src, cat, users, q] = await Promise.all([
      api.searches.list(listParams),
      api.sources.list(),
      api.categories(),
      canManageUsers ? api.workspaceUsers.pickers().then((r) => workspacePickersAsUsers(r.items)) : Promise.resolve(null),
      api.me.quotas().catch(() => null),
    ]);
    setSearches(s);
    setAllSources(src);
    setCategories(cat);
    if (users) setTeamUsers(users);
    if (q) setQuotas(q);
    setError('');
  }, [canManageUsers, ownerFilter]);

  const { autoRefresh, initialLoading } = useAutoRefreshPolling({
    hasData: () => searches.length > 0,
    load: async () => {
      try {
        await fetchSearches();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load searches');
        throw e;
      }
    },
    autoRefresh: {
      intervalMs: 30000,
      deps: [canManageUsers, ownerFilter],
      storageKey: 'searches-auto-refresh',
    },
  });

  const reload = useCallback(async () => {
    await autoRefresh.refresh();
  }, [autoRefresh]);

  const capabilities = useMemo(() => resolveCapabilities(me, quotas), [me, quotas]);
  const planLimits = useMemo(
    () =>
      searchPlanValidationLimits(
        capabilities,
        editId ? undefined : (quotas?.usage?.searches ?? searches.length),
      ),
    [capabilities, editId, quotas?.usage?.searches, searches.length],
  );

  function setCategory(category: string) {
    setForm((prev) => clearIrrelevantFields(prev, category));
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const validationError = validateSearchForm(form, planLimits);
    if (validationError) {
      setError(validationError);
      return;
    }
    const body = {
      name: form.name.trim(),
      category: form.category,
      source_names: form.source_names,
      filters_json: buildSearchFilters(form),
      visibility: form.visibility,
    };
    try {
      if (editId) {
        const updated = await api.searches.update(editId, body);
        toast(updated.save_message ?? 'Saved. New scrape job queued.', 'success');
        if (updated.scrape_job_id) {
          setActiveJobId(updated.scrape_job_id);
          setActiveSearchId(editId);
        }
      } else {
        const { data, planWarning } = await api.searches.create(body);
        toast(data.save_message ?? 'Search created', 'success');
        if (data.scrape_job_id) {
          setActiveJobId(data.scrape_job_id);
          setActiveSearchId(data.id);
        }
        const warn = shouldShowPlanLimitWarning(capabilities)
          ? planWarningMessage(planWarning)
          : null;
        if (warn) toast(warn, 'error');
      }
      setForm(resetSearchFormForCreate('vehicles', me));
      setEditId(null);
      setError('');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  }

  function startEdit(s: Search) {
    setEditId(s.id);
    setForm(searchToAdminForm(s));
    setError('');
  }

  function startDuplicate(s: Search) {
    setEditId(null);
    setForm(duplicateSearchToAdminForm(s));
    setError('');
  }

  function cancelEdit() {
    setEditId(null);
    setForm(resetSearchFormForCreate('vehicles', me));
    setError('');
  }

  const remove = useCallback(async (id: string) => {
    setDeleteId(id);
  }, []);

  const runScrape = useCallback(
    async (id: string, force = false) => {
      setRunningId(id);
      try {
        const validation = await api.scrape.validateSearch(id);
        if (validation.warnings.length) {
          validation.warnings.forEach((w) => toast(w, 'error'));
          if (!validation.ok && !force) {
            setScrapeWarningId(id);
            return;
          }
        }
        const result = await api.scrape.runSearch(id);
        if (result.job_id) {
          setActiveJobId(result.job_id);
          setActiveSearchId(id);
        }
        toast(
          result.jobs_queued
            ? result.job_id
              ? `Scrape job queued (${result.total_items ?? result.jobs_queued} items)`
              : `Queued ${result.jobs_queued} scrape job(s)`
            : 'No jobs queued — check search text and sources',
          result.jobs_queued ? 'success' : 'error'
        );
        result.warnings.forEach((w) => toast(w, 'error'));
      } catch (err) {
        toast(runSearchErrorMessage(err, 'Scrape failed'), 'error');
      } finally {
        setRunningId(null);
      }
    },
    [toast]
  );

  const maxSourcesPerSearch = planLimits.maxSourcesPerSearch;
  const accessibleSourceCount = planLimits.accessibleSources;

  function toggleSource(name: string) {
    setForm((prev) => {
      if (prev.source_names.includes(name)) {
        return {
          ...prev,
          source_names: prev.source_names.filter((n) => n !== name),
        };
      }
      if (maxSourcesPerSearch != null && prev.source_names.length >= maxSourcesPerSearch) {
        setError(
          accessibleSourceCount != null
            ? searchSourceLimitMessage(accessibleSourceCount, maxSourcesPerSearch)
            : `Your plan allows up to ${maxSourcesPerSearch} sources per search.`,
        );
        return prev;
      }
      setError('');
      return {
        ...prev,
        source_names: [...prev.source_names, name],
      };
    });
  }

  function selectAllSources() {
    const names =
      maxSourcesPerSearch != null
        ? enabledSources.slice(0, maxSourcesPerSearch).map((s) => s.name)
        : enabledSources.map((s) => s.name);
    setForm((prev) => ({
      ...prev,
      source_names: names,
    }));
    if (
      maxSourcesPerSearch != null &&
      enabledSources.length > maxSourcesPerSearch &&
      accessibleSourceCount != null
    ) {
      setError(searchSourceLimitMessage(accessibleSourceCount, maxSourcesPerSearch));
    }
  }

  function clearAllSources() {
    setForm((prev) => ({ ...prev, source_names: [] }));
  }

  function ownerLabel(search: Search): string {
    if (search.owner_name?.trim()) return search.owner_name.trim();
    if (search.owner_email?.trim()) return search.owner_email.trim();
    if (search.user_id) return search.user_id.slice(0, 8);
    return 'Legacy (unassigned)';
  }

  const canEditSearch = useCallback(
    (search: Search): boolean => {
      if (readOnly) return false;
      if (search.can_edit != null) return search.can_edit;
      if (canManageWorkspaceMembers) return true;
      const isOwner =
        (search.user_id != null && search.user_id === me?.id) ||
        (search.creator_user_id != null && search.creator_user_id === me?.id);
      if (isOwner) return true;
      if (!search.is_shared) return true;
      return search.share_permissions?.can_edit ?? false;
    },
    [readOnly, me?.id, canManageWorkspaceMembers]
  );

  const canRunSearch = useCallback(
    (search: Search): boolean => {
      if (readOnly) return false;
      if (search.can_run != null) return search.can_run;
      return canRun && !readOnly;
    },
    [canRun, readOnly]
  );

  const canRemoveSearch = useCallback(
    (search: Search): boolean => canEditSearch(search) && !search.is_shared,
    [canEditSearch]
  );

  const enabledSources = useMemo(
    () => allSources.filter((s) => s.is_enabled),
    [allSources]
  );

  const sourceLimitHint =
    maxSourcesPerSearch != null
      ? accessibleSourceCount != null
        ? searchSourceLimitMessage(accessibleSourceCount, maxSourcesPerSearch)
        : `Your plan allows up to ${maxSourcesPerSearch} sources per search.`
      : undefined;

  const editingSearch = editId ? searches.find((s) => s.id === editId) : null;
  const canEditVisibility = editingSearch ? canEditSearchVisibility(me, editingSearch) : false;
  const visibilityDirty =
    editId != null &&
    (visibilityDraft === 'private' ? 'private' : 'workspace') !==
      (savedVisibility === 'workspace' ? 'workspace' : 'private');

  useEffect(() => {
    if (!editingSearch) return;
    const vis = editingSearch.visibility === 'workspace' || editingSearch.visibility === 'organization'
      ? 'workspace'
      : 'private';
    setVisibilityDraft(vis);
    setSavedVisibility(vis);
    setVisibilityStatus('idle');
  }, [editingSearch?.id, editingSearch?.visibility]);

  async function saveVisibility() {
    if (!editId || !canEditVisibility) return;
    setVisibilitySaving(true);
    setVisibilityStatus('saving');
    try {
      const updated = await api.searches.update(editId, { visibility: visibilityDraft });
      setSavedVisibility(visibilityDraft);
      setForm((prev) => ({ ...prev, visibility: visibilityDraft }));
      setSearches((prev) => prev.map((s) => (s.id === editId ? { ...s, ...updated } : s)));
      setVisibilityStatus('saved');
      toast('Visibility saved', 'success');
    } catch (err) {
      setVisibilityStatus('error');
      setError(err instanceof Error ? err.message : 'Failed to save visibility');
    } finally {
      setVisibilitySaving(false);
    }
  }

  function cancelVisibilityEdit() {
    setVisibilityDraft(savedVisibility);
    setForm((prev) => ({ ...prev, visibility: savedVisibility }));
    setVisibilityStatus('idle');
    setError('');
  }

  const canManageJobActions =
    (me?.permissions.canManageWorkspaceJobs ?? false) || (me?.permissions.canManageJobs ?? false);

  const enabledBySource = useMemo(
    () => Object.fromEntries(allSources.map((s) => [s.name, s.is_enabled])),
    [allSources]
  );

  const searchColumns = useMemo<DataTableColumn<Search>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        width: 160,
        minWidth: 100,
        maxWidth: 280,
        sortable: true,
        sortValue: (s) => s.name,
        csvValue: (s) => s.name,
        render: (s) => <TruncatedCell value={s.name} />,
      },
      {
        id: 'createdBy',
        header: 'Created by',
        width: 140,
        minWidth: 100,
        maxWidth: 220,
        sortable: true,
        sortValue: (s) => ownerLabel(s),
        render: (s) => <TruncatedCell value={ownerLabel(s)} />,
      },
      {
        id: 'visibility',
        header: 'Visibility',
        width: 110,
        minWidth: 90,
        maxWidth: 140,
        sortable: true,
        sortValue: (s) => s.visibility || 'private',
        render: (s) => (
          <StatusBadge variant={s.visibility === 'workspace' || s.visibility === 'organization' ? 'info' : 'neutral'}>
            {visibilityBadgeLabel(s.visibility ?? (s.is_shared ? 'workspace' : 'private'))}
          </StatusBadge>
        ),
      },
      {
        id: 'category',
        header: 'Category',
        width: 110,
        minWidth: 80,
        maxWidth: 160,
        render: (s) => <TruncatedCell value={s.category} />,
      },
      {
        id: 'searchText',
        header: 'Search text',
        width: 240,
        minWidth: 140,
        maxWidth: 420,
        render: (s) => <TruncatedCell value={s.filters_json?.search_text} />,
      },
      {
        id: 'sources',
        header: 'Sources',
        width: 220,
        minWidth: 140,
        maxWidth: 400,
        render: (s) => (
          <SourceChips sources={s.source_names} enabledBySource={enabledBySource} variant="compact" />
        ),
      },
      {
        id: 'status',
        header: 'Status',
        width: 200,
        minWidth: 140,
        maxWidth: 280,
        render: (s) => {
          if (s.is_enabled) {
            return <span className="badge badge-ok">Active</span>;
          }
          const label = s.pause_reason_label ?? humanizeReason(s.pause_reason).title;
          const since = s.paused_since
            ? new Date(s.paused_since).toLocaleDateString()
            : null;
          return (
            <div className="search-pause-status">
              <span className="badge badge-warning">Paused</span>
              <div className="muted search-pause-status__reason">{label}</div>
              {since ? <div className="muted search-pause-status__since">Since {since}</div> : null}
              {s.pause_action ? (
                <div className="muted search-pause-status__action">{s.pause_action}</div>
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        width: TABLE_ACTION_MIN_WIDTH.searches,
        minWidth: TABLE_ACTION_MIN_WIDTH.searches,
        resizable: false,
        actions: true,
        className: tableActionsColumnClass('searches'),
        render: (s) => (
          <TableActionBar>
            <Link to={`/searches/${s.id}/links`} className="btn btn-sm">
              View results
            </Link>
            {canRunSearch(s) ? (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={runningId === s.id}
                onClick={() => runScrape(s.id)}
              >
                {runningId === s.id ? '…' : 'Run now'}
              </button>
            ) : null}
            {canEditSearch(s) ? (
              <>
                <button type="button" className="btn btn-sm" onClick={() => startEdit(s)}>
                  Edit
                </button>
                <button type="button" className="btn btn-sm" onClick={() => startDuplicate(s)}>
                  Duplicate
                </button>
                {canRemoveSearch(s) ? (
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => remove(s.id)}>
                    Remove
                  </button>
                ) : null}
              </>
            ) : null}
          </TableActionBar>
        ),
      },
    ],
    [runningId, remove, runScrape, enabledBySource, canEditSearch, canRunSearch, canRemoveSearch]
  );

  return (
    <div
      className={`admin-page admin-page--searches${
        !readOnly && canCreate && isCompactNav ? ' admin-page--with-fab' : ''
      }`}
    >
      <PageHeader
        title="Searches"
        subtitle="Reusable search definitions — filters, categories and sources"
      >
        {!readOnly && (
          <Link to="/setup" className="btn btn-primary">
            Quick setup
          </Link>
        )}
        <AutoRefreshToolbar control={autoRefresh} intervalLabel="Every 30s" />
      </PageHeader>

      {error ? <ErrorBanner message={error} onDismiss={() => setError('')} /> : null}

      <AdminPageSections>
        {!readOnly && canCreate && (
          <div id="search-create-panel">
          <SearchAdminFormPanels
            storageScope={`${PANEL_SCOPE}-form-${editId ?? 'create'}`}
            formKey={editId ?? 'create'}
            form={form}
            categories={categories}
            sources={allSources}
            editId={editId}
            isAdmin={isAdmin}
            onChange={setForm}
            onCategoryChange={setCategory}
            onToggleSource={toggleSource}
            onSelectAllSources={selectAllSources}
            onClearAllSources={clearAllSources}
            sourceLimitHint={sourceLimitHint}
            showVisibilitySection={!editId}
            showWorkspaceVisibilityOption={showWorkspaceVisibility}
            onSubmit={save}
            onCancelEdit={cancelEdit}
          />
          </div>
        )}

        {editId && editingSearch ? (
          <CollapsiblePanel
            id="search-share"
            storageScope={PANEL_SCOPE}
            title="Sharing & access"
            subtitle={`Owner: ${ownerLabel(editingSearch)}`}
            defaultExpanded={false}
          >
            <SearchVisibilityPanel
              value={visibilityDraft}
              editable={canEditVisibility}
              showWorkspaceOption={showWorkspaceVisibility}
              onChange={(visibility) => {
                setVisibilityDraft(visibility);
                setForm((prev) => ({ ...prev, visibility }));
                setVisibilityStatus('idle');
              }}
            />
            {canEditVisibility ? (
              <div className="search-visibility-actions">
                {visibilityStatus === 'saving' ? (
                  <p className="muted">Saving visibility…</p>
                ) : visibilityStatus === 'saved' && !visibilityDirty ? (
                  <p className="muted">✓ Visibility saved</p>
                ) : visibilityDirty ? (
                  <p className="muted">Visibility changed — save to apply</p>
                ) : null}
                <div className="search-visibility-actions__buttons">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={!visibilityDirty || visibilitySaving}
                    onClick={() => void saveVisibility()}
                  >
                    Save changes
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    disabled={!visibilityDirty || visibilitySaving}
                    onClick={cancelVisibilityEdit}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}
            {(canManageUsers || canManageWorkspaceMembers) && (
              <>
                <SearchSharePanel
                  searchId={editId}
                  searchOwnerId={editingSearch.user_id}
                  currentUserId={me?.id}
                  canManageShares={canManageUsers || canManageWorkspaceMembers}
                />
                <SearchAclPanel
                  searchId={editId}
                  searchOwnerId={editingSearch.user_id}
                  currentUserId={me?.id}
                  canManageAcl={canManageUsers || canManageWorkspaceMembers}
                />
              </>
            )}
            {editingSearch.user_id === me?.id ? (
              <div className="search-transfer-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  onClick={() => setTransferSearch(editingSearch)}
                >
                  Transfer ownership…
                </button>
              </div>
            ) : null}
          </CollapsiblePanel>
        ) : null}

        {transferSearch ? (
          <SearchTransferDialog
            search={transferSearch}
            currentUserId={me?.id}
            onClose={() => setTransferSearch(null)}
            onTransferred={(updated) => {
              setSearches((prev) => prev.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)));
              if (editId === updated.id) {
                setForm(searchToAdminForm(updated));
              }
              toast('Search ownership transferred', 'success');
            }}
          />
        ) : null}

        <CollapsiblePanel
          id="existing-searches"
          storageScope={PANEL_SCOPE}
          title="Existing searches"
          badge={initialLoading ? '…' : searches.length}
          defaultExpanded={!editId}
          unmountWhenCollapsed
          loading={initialLoading}
        >
          {canManageUsers && teamUsers.length > 0 ? (
            <Toolbar>
              <FilterSelect label="Owner" htmlFor="searches-owner-filter">
                <select
                  id="searches-owner-filter"
                  className="filter-select"
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                >
                  <option value="">All owners</option>
                  {teamUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name?.trim() || u.email || u.id}
                    </option>
                  ))}
                </select>
              </FilterSelect>
            </Toolbar>
          ) : null}
          {searches.length === 0 ? (
            <EmptyState
              title="No searches created yet"
              description="Create a search to define what listings to monitor and which sources to use."
              action={
                !readOnly && canCreate ? (
                  <span className="muted">Expand Create search above to add your first search.</span>
                ) : undefined
              }
            />
          ) : (
            <DataTable
              columns={searchColumns}
              rows={searches}
              getRowKey={(s) => s.id}
              columnStorageKey="searches"
              toolbar={{
                searchPlaceholder: 'Filter searches…',
                onRefresh: () => void reload(),
                rightSlot: canExport ? <ExportButtons path="/exports/searches" /> : undefined,
              }}
            />
          )}
        </CollapsiblePanel>

        {activeJobId && (
          <CollapsiblePanel
            id="active-job"
            storageScope={PANEL_SCOPE}
            title="Active scrape job"
            defaultExpanded
          >
            <JobProgressCard
              jobId={activeJobId}
              title="Search scrape job"
              onDismiss={() => setActiveJobId(null)}
              canManageActions={canManageJobActions}
            />
          </CollapsiblePanel>
        )}

        {activeSearchId && (
          <CollapsiblePanel
            id="recent-jobs"
            storageScope={PANEL_SCOPE}
            title="Recent jobs"
            subtitle="Jobs for the selected search"
            defaultExpanded
          >
            <RecentJobsPanel
              searchId={activeSearchId}
              onSelectJob={(id) => setActiveJobId(id)}
            />
          </CollapsiblePanel>
        )}
      </AdminPageSections>

      {deleteId && (
        <ConfirmDeleteModal
          entityType="search"
          entityId={deleteId}
          onClose={() => setDeleteId(null)}
          onDone={(msg) => {
            toast(msg, 'success');
            void reload();
          }}
        />
      )}
      <ConfirmActionModal
        open={Boolean(scrapeWarningId)}
        title="Run scrape with warnings?"
        description="Validation reported warnings. You can run the scrape anyway or cancel and fix the search first."
        confirmLabel="Run anyway"
        loading={runningId === scrapeWarningId}
        onCancel={() => setScrapeWarningId(null)}
        onConfirm={async () => {
          const id = scrapeWarningId;
          setScrapeWarningId(null);
          if (id) await runScrape(id, true);
        }}
      />
      {!readOnly && canCreate && isCompactNav ? (
        <FloatingActionButton
          label="Create search"
          onClick={() =>
            document.getElementById('search-create-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        />
      ) : null}
    </div>
  );
}
